import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import styles from '../../styles/TagContextSelector.module.css';
import { SparkTagDimensionSelection } from '../../types/sparkTypes';
import { TagPatternDisplayDimension } from '../../utils/tagPatterns';

export interface TagContextSelectorConstraints {
  allowedDimensionFqns?: Set<string>;
  allowedElementFqnsByDimension?: Map<string, Set<string>>;
  strictDimensions?: Set<string>;
}

interface TagContextSelectorProps {
  mode: 'project' | 'creative';
  dimensions: TagPatternDisplayDimension[];
  value: SparkTagDimensionSelection[] | null | undefined;
  constraints?: TagContextSelectorConstraints;
  disabled?: boolean;
  isPersisting?: boolean;
  error?: string | null;
  onPersist: (pattern: SparkTagDimensionSelection[]) => Promise<void> | void;
  onChange?: (pattern: SparkTagDimensionSelection[]) => void;
  variant?: 'default' | 'header' | 'compact';
  hideInactiveHeaderChips?: boolean; // when header variant, hide chips that are not selected
  disableRemove?: boolean; // hide remove buttons even when interactive
}

interface DimensionMeta {
  dimensionFqn: string;
  elementFqns: string[];
  isOptional: boolean;
  isStrict: boolean;
}

const emptyPattern: SparkTagDimensionSelection[] = [];

const normaliseElements = (elements: string[], dimensionElements: string[]) => {
  if (elements.length <= 1) {
    return elements;
  }
  const order = new Map<string, number>();
  dimensionElements.forEach((elementFqn, index) => order.set(elementFqn, index));
  return Array.from(new Set(elements)).sort((a, b) => {
    const indexA = order.get(a) ?? Number.MAX_SAFE_INTEGER;
    const indexB = order.get(b) ?? Number.MAX_SAFE_INTEGER;
    return indexA - indexB;
  });
};

const createDimensionMeta = (
  dimension: TagPatternDisplayDimension,
  constraints?: TagContextSelectorConstraints
): DimensionMeta => {
  const constraintStrict = constraints?.strictDimensions?.has(dimension.dimensionFqn);
  return {
    dimensionFqn: dimension.dimensionFqn,
    elementFqns: dimension.elements.map((el) => el.fqn),
    isOptional: dimension.isOptional,
    isStrict: constraintStrict ?? dimension.isStrict,
  };
};

const buildEntry = (
  meta: DimensionMeta,
  elementFqns: string[],
  fallback?: SparkTagDimensionSelection
): SparkTagDimensionSelection => ({
  dimension_fqn: meta.dimensionFqn,
  element_fqns: elementFqns,
  is_optional: fallback?.is_optional ?? meta.isOptional,
  is_strict: fallback?.is_strict ?? meta.isStrict,
});

const sortPattern = (
  pattern: SparkTagDimensionSelection[],
  dimensionOrder: Map<string, number>
) => {
  return [...pattern].sort((a, b) => {
    const indexA = dimensionOrder.get(a.dimension_fqn) ?? Number.MAX_SAFE_INTEGER;
    const indexB = dimensionOrder.get(b.dimension_fqn) ?? Number.MAX_SAFE_INTEGER;
    return indexA - indexB;
  });
};

const arePatternsEqual = (
  left: SparkTagDimensionSelection[],
  right: SparkTagDimensionSelection[]
) => {
  if (left.length !== right.length) {
    return false;
  }

  const rightMap = new Map<string, SparkTagDimensionSelection>();
  right.forEach((entry) => rightMap.set(entry.dimension_fqn, entry));

  for (const entry of left) {
    const candidate = rightMap.get(entry.dimension_fqn);
    if (!candidate) {
      return false;
    }

    if (entry.is_optional !== candidate.is_optional || entry.is_strict !== candidate.is_strict) {
      return false;
    }

    if (entry.element_fqns.length !== candidate.element_fqns.length) {
      return false;
    }

    const leftElements = [...entry.element_fqns].sort();
    const rightElements = [...candidate.element_fqns].sort();
    for (let index = 0; index < leftElements.length; index += 1) {
      if (leftElements[index] !== rightElements[index]) {
        return false;
      }
    }
  }

  return true;
};

const useOutsideClickHandler = (
  isActive: boolean,
  refs: React.RefObject<HTMLElement>[] | (() => (HTMLElement | null)[]),
  handler: () => void
) => {
  useEffect(() => {
    if (!isActive) {
      return;
    }

    const resolveElements = () => {
      if (typeof refs === 'function') {
        return refs().filter(Boolean) as HTMLElement[];
      }
      return refs
        .map((ref) => ref.current)
        .filter((element): element is HTMLElement => Boolean(element));
    };

    const handleClick = (event: MouseEvent) => {
      const elements = resolveElements();
      const target = event.target as Node;
      if (elements.some((element) => element.contains(target))) {
        return;
      }
      handler();
    };

    document.addEventListener('mousedown', handleClick, true);
    return () => {
      document.removeEventListener('mousedown', handleClick, true);
    };
  }, [isActive, refs, handler]);
};

const TagContextSelector: React.FC<TagContextSelectorProps> = ({
  mode,
  dimensions,
  value,
  constraints,
  disabled = false,
  isPersisting = false,
  error = null,
  onPersist,
  onChange,
  variant = 'default',
  hideInactiveHeaderChips = false,
  disableRemove = false,
}) => {
  const dimensionOrder = useMemo(() => {
    const order = new Map<string, number>();
    dimensions.forEach((dimension, index) => order.set(dimension.dimensionFqn, index));
    return order;
  }, [dimensions]);

  const isHeaderVariant = variant === 'header';
  const isCompactVariant = variant === 'compact';

  const allowedDimensionFqns = useMemo(() => {
    if (mode === 'creative' && constraints?.allowedDimensionFqns) {
      return constraints.allowedDimensionFqns;
    }
    return undefined;
  }, [constraints?.allowedDimensionFqns, mode]);

  const relevantDimensions = useMemo(() => {
    if (!allowedDimensionFqns) {
      return dimensions;
    }
    return dimensions.filter((dimension) => allowedDimensionFqns.has(dimension.dimensionFqn));
  }, [allowedDimensionFqns, dimensions]);

  const allowedElementsFor = useCallback((dimension: TagPatternDisplayDimension): string[] => {
    const meta = createDimensionMeta(dimension, constraints);
    // If allowedDimensionFqns is provided and this dimension is not included, hide entirely
    const allowedDims = constraints?.allowedDimensionFqns;
    if (allowedDims && !allowedDims.has(meta.dimensionFqn)) {
      return [];
    }
    const allowedSet = constraints?.allowedElementFqnsByDimension?.get(meta.dimensionFqn);
    if (allowedSet) {
      return dimension.elements.filter(e => allowedSet.has(e.fqn)).map(e => e.fqn);
    }
    // Wildcard in schema: fallback to all active elements
    return dimension.elements.filter(e => e.isActive).map(e => e.fqn);
  }, [constraints]);

  const valuePattern = useMemo(() => value ?? emptyPattern, [value]);

  const normalisedInitialPattern = useMemo(() => {
    const entryMap = new Map<string, SparkTagDimensionSelection>();
    valuePattern.forEach((entry) => {
      if (dimensionOrder.has(entry.dimension_fqn)) {
        entryMap.set(entry.dimension_fqn, {
          ...entry,
          element_fqns: Array.from(new Set(entry.element_fqns)),
        });
      }
    });

    if (mode === 'project') {
      relevantDimensions.forEach((dimension) => {
        if (!entryMap.has(dimension.dimensionFqn)) {
          entryMap.set(
            dimension.dimensionFqn,
            buildEntry(createDimensionMeta(dimension, constraints), [])
          );
        }
      });
    }

    return sortPattern(Array.from(entryMap.values()), dimensionOrder);
  }, [constraints, dimensionOrder, mode, relevantDimensions, valuePattern]);

  const [draftPattern, setDraftPattern] = useState<SparkTagDimensionSelection[]>(
    normalisedInitialPattern
  );
  const lastSyncedPatternRef = useRef<SparkTagDimensionSelection[]>(normalisedInitialPattern);
  const selfPersistingRef = useRef<boolean>(false);
  const pendingPatternRef = useRef<SparkTagDimensionSelection[] | null>(null);
  const [openDimension, setOpenDimension] = useState<string | null>(null);
  const [persistError, setPersistError] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const activeRowRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (openDimension || isPersisting) {
      return;
    }

    // If we initiated persist, wait for value to match pending
    if (selfPersistingRef.current) {
      const pending = pendingPatternRef.current || [];
      if (arePatternsEqual(pending, normalisedInitialPattern)) {
        // Draft already has correct value from updateDimensionSelection, just update ref and clear flags
        lastSyncedPatternRef.current = normalisedInitialPattern;
        selfPersistingRef.current = false;
        pendingPatternRef.current = null;
      }
      return;
    }

    if (arePatternsEqual(lastSyncedPatternRef.current, normalisedInitialPattern)) {
      return;
    }

    lastSyncedPatternRef.current = normalisedInitialPattern;
    setDraftPattern(normalisedInitialPattern);
  }, [isPersisting, normalisedInitialPattern, openDimension]);

  useEffect(() => {
    if (error) {
      setPersistError(error);
    }
  }, [error]);

  const persistPattern = useCallback(
    async (nextPattern: SparkTagDimensionSelection[]) => {
      try {
        selfPersistingRef.current = true;
        pendingPatternRef.current = nextPattern;
        await onPersist(nextPattern);
        setPersistError(null);
      } catch (persistFailure) {
        lastSyncedPatternRef.current = normalisedInitialPattern;
        setDraftPattern(normalisedInitialPattern);
        if (persistFailure instanceof Error) {
          setPersistError(persistFailure.message);
        } else {
          setPersistError('Failed to update context');
        }
        selfPersistingRef.current = false;
        pendingPatternRef.current = null;
        throw persistFailure;
      }
    },
    [normalisedInitialPattern, onPersist]
  );

  const handleCloseDropdown = useCallback(async () => {
    if (!openDimension) {
      return;
    }

    setOpenDimension(null);

    if (arePatternsEqual(draftPattern, normalisedInitialPattern)) {
      return;
    }

    try {
      await persistPattern(draftPattern);
    } catch {
      // errors handled inside persistPattern
    }
  }, [draftPattern, normalisedInitialPattern, openDimension, persistPattern]);

  useOutsideClickHandler(
    Boolean(openDimension),
    () => [dropdownRef.current, activeRowRef.current],
    () => {
      if (!isPersisting) {
        void handleCloseDropdown();
      }
    }
  );

  const handleToggleDimension = useCallback(
    (dimensionFqn: string, rowElement: HTMLDivElement | null) => {
      if (disabled || isPersisting) {
        return;
      }

      setPersistError(null);
      if (openDimension === dimensionFqn) {
        void handleCloseDropdown();
        return;
      }

      setOpenDimension(dimensionFqn);
      activeRowRef.current = rowElement;
    },
    [disabled, handleCloseDropdown, isPersisting, openDimension]
  );

  const updateDimensionSelection = useCallback(
    (
      meta: DimensionMeta,
      elementFqns: string[],
      fallback?: SparkTagDimensionSelection,
      persistMode: 'immediate' | 'defer' = 'defer'
    ) => {
      const nextElements = normaliseElements(elementFqns, meta.elementFqns);
      const nextPattern = draftPattern.filter(
        (entry) => entry.dimension_fqn !== meta.dimensionFqn
      );

      if (mode === 'project') {
        nextPattern.push(buildEntry(meta, nextElements, fallback));
      } else {
        if (nextElements.length === 0) {
          if (meta.isStrict) {
            // Should never happen due to upstream guards; restore fallback if present
            if (fallback) {
              nextPattern.push(buildEntry(meta, fallback.element_fqns, fallback));
            }
          } else {
            // Non-strict dimension with no elements: include with empty array so server
            // doesn't fall back to project defaults (intersect_patterns treats missing
            // dimensions as "use project elements")
            nextPattern.push(buildEntry(meta, [], fallback));
          }
        } else {
          nextPattern.push(buildEntry(meta, nextElements, fallback));
        }
      }

      const sorted = sortPattern(nextPattern, dimensionOrder);
      setDraftPattern(sorted);
      onChange?.(sorted);

      if (persistMode === 'immediate') {
        lastSyncedPatternRef.current = sorted;
        void persistPattern(sorted);
      }
    },
    [dimensionOrder, draftPattern, mode, onChange, persistPattern]
  );

  const handleRemoveChip = useCallback(
    (dimension: TagPatternDisplayDimension, elementFqn: string) => {
      const meta = createDimensionMeta(dimension, constraints);
      const selection = draftPattern.find((entry) => entry.dimension_fqn === meta.dimensionFqn);
      const currentElements = selection?.element_fqns ?? [];
      if (!currentElements.includes(elementFqn)) {
        return;
      }

      if ((meta.isStrict && currentElements.length === 1)) {
        return;
      }

      const nextElements = currentElements.filter((fqn) => fqn !== elementFqn);
      updateDimensionSelection(meta, nextElements, selection, 'immediate');
    },
    [constraints, draftPattern, updateDimensionSelection]
  );

  const handleToggleElement = useCallback(
    (dimension: TagPatternDisplayDimension, elementFqn: string) => {
      const meta = createDimensionMeta(dimension, constraints);
      const selection = draftPattern.find((entry) => entry.dimension_fqn === meta.dimensionFqn);
      const currentElements = selection?.element_fqns ?? [];
      const isSelected = currentElements.includes(elementFqn);

      if (isSelected) {
        if (meta.isStrict && currentElements.length === 1) {
          return;
        }
        updateDimensionSelection(
          meta,
          currentElements.filter((fqn) => fqn !== elementFqn),
          selection,
          'defer'
        );
        return;
      }

      const allowedElements = constraints?.allowedElementFqnsByDimension?.get(meta.dimensionFqn);
      if (allowedElements && !allowedElements.has(elementFqn)) {
        return;
      }

      updateDimensionSelection(meta, [...currentElements, elementFqn], selection, 'defer');
    },
    [constraints, draftPattern, updateDimensionSelection]
  );

  const renderDropdown = useCallback(
    (dimension: TagPatternDisplayDimension, selection?: SparkTagDimensionSelection) => {
      if (openDimension !== dimension.dimensionFqn) {
        return null;
      }

      const meta = createDimensionMeta(dimension, constraints);
      const allowedElements = constraints?.allowedElementFqnsByDimension?.get(meta.dimensionFqn);
      let options = dimension.elements.filter((element) => {
        if (allowedElements) {
          return allowedElements.has(element.fqn);
        }
        return element.isActive;
      });

      const selectedElementFqns = new Set(selection?.element_fqns ?? []);
      // For compact variant, only list unselected options
      if (isCompactVariant) {
        options = options.filter((opt) => !selectedElementFqns.has(opt.fqn));
      }

      return (
        <div className={styles.dropdown} ref={dropdownRef}>
          {options.length === 0 ? (
            <div className={styles.emptyOptions}>No options available</div>
          ) : (
            <ul className={styles.optionList}>
              {options.map((option) => {
                const isSelected = selectedElementFqns.has(option.fqn);
                const willBecomeEmpty = isSelected && meta.isStrict && selectedElementFqns.size === 1;
                return (
                  <li
                    key={option.fqn}
                    className={`${styles.optionItem} ${isSelected ? styles.optionItemSelected : ''}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      if (willBecomeEmpty) {
                        return;
                      }
                      handleToggleElement(dimension, option.fqn);
                    }}
                    role="option"
                    aria-selected={isSelected}
                  >
                    <div className={styles.optionContent}>
                      <span className={styles.optionLabel}>{option.name}</span>
                      {isSelected && <CheckIcon fontSize="small" className={styles.optionIcon} />}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      );
    },
    [constraints, handleToggleElement, openDimension, isCompactVariant]
  );

  const headerChips = useMemo(() => {
    if (!isHeaderVariant) {
      return [] as Array<{
        dimension: TagPatternDisplayDimension;
        elementFqn: string;
        elementName: string;
      }>;
    }

    const allowedElements = constraints?.allowedElementFqnsByDimension;
    const items: Array<{ dimension: TagPatternDisplayDimension; elementFqn: string; elementName: string }> = [];

    dimensions.forEach((dimension) => {
      const meta = createDimensionMeta(dimension, constraints);
      const allowedSet = allowedElements?.get(meta.dimensionFqn);
      dimension.elements.forEach((element) => {
        const isAllowed = allowedSet ? allowedSet.has(element.fqn) : element.isActive;
        if (!isAllowed) {
          return;
        }
        items.push({
          dimension,
          elementFqn: element.fqn,
          elementName: element.name,
        });
      });
    });

    return items;
  }, [constraints, dimensions, isHeaderVariant]);

  const selectionLookup = useMemo(() => {
    const map = new Map<string, Set<string>>();
    draftPattern.forEach((entry) => {
      map.set(entry.dimension_fqn, new Set(entry.element_fqns));
    });
    return map;
  }, [draftPattern]);

  // Precompute compact variant helpers unconditionally to satisfy hooks rules
  const eligibleDimensions = useMemo(() => {
    return relevantDimensions.filter((d) => {
      const allowed = new Set(allowedElementsFor(d));
      const selected = new Set(
        draftPattern.find((e) => e.dimension_fqn === d.dimensionFqn)?.element_fqns || []
      );
      selected.forEach((f) => allowed.delete(f));
      return allowed.size > 0;
    });
  }, [allowedElementsFor, relevantDimensions, draftPattern]);

  const selectedChips = useMemo(() => {
    const chips: Array<{ dimension: TagPatternDisplayDimension; elementFqn: string; elementName: string }>=[];
    const byDim = new Map<string, SparkTagDimensionSelection>();
    draftPattern.forEach(e => byDim.set(e.dimension_fqn, e));
    relevantDimensions.forEach((dim) => {
      const sel = byDim.get(dim.dimensionFqn);
      if (!sel) return;
      const nameByFqn = new Map(dim.elements.map(el => [el.fqn, el.name] as const));
      (sel.element_fqns || []).forEach(fqn => {
        const label = nameByFqn.get(fqn) || fqn;
        chips.push({ dimension: dim, elementFqn: fqn, elementName: label });
      });
    });
    return chips;
  }, [draftPattern, relevantDimensions]);

  const handleToggleHeaderChip = useCallback(
    (dimension: TagPatternDisplayDimension, elementFqn: string) => {
      const meta = createDimensionMeta(dimension, constraints);
      const selection = draftPattern.find((entry) => entry.dimension_fqn === meta.dimensionFqn);
      const currentElements = selection?.element_fqns ?? [];
      const isSelected = currentElements.includes(elementFqn);

      if (isSelected) {
        if (meta.isStrict && currentElements.length === 1) {
          return;
        }
        updateDimensionSelection(
          meta,
          currentElements.filter((fqn) => fqn !== elementFqn),
          selection,
          'immediate'
        );
        return;
      }

      const allowedElements = constraints?.allowedElementFqnsByDimension?.get(meta.dimensionFqn);
      if (allowedElements && !allowedElements.has(elementFqn)) {
        return;
      }

      updateDimensionSelection(meta, [...currentElements, elementFqn], selection, 'immediate');
    },
    [constraints, draftPattern, updateDimensionSelection]
  );

  if (isHeaderVariant) {
    const chips = (hideInactiveHeaderChips
      ? headerChips.filter(({ dimension, elementFqn }) =>
          selectionLookup.get(dimension.dimensionFqn)?.has(elementFqn) ?? false
        )
      : headerChips);
    return (
      <div className={`${styles.container} ${styles.headerVariant}`}>
        <div className={styles.headerChipRow}>
          {chips.map(({ dimension, elementFqn, elementName }) => {
            const isSelected = selectionLookup.get(dimension.dimensionFqn)?.has(elementFqn) ?? false;
            const meta = createDimensionMeta(dimension, constraints);
            const isDisabled = disabled || isPersisting;
            return (
              <button
                type="button"
                key={`${dimension.dimensionFqn}:${elementFqn}`}
                className={`${styles.headerChip} ${isSelected ? styles.headerChipSelected : styles.headerChipInactive}`}
                onClick={() => handleToggleHeaderChip(dimension, elementFqn)}
                disabled={isDisabled}
                title={`${dimension.name}: ${elementName}`}
              >
                <span className={styles.headerChipLabel}>
                  {elementName}
                  {!meta.isOptional && isSelected && <span className={styles.headerChipRequired}>*</span>}
                </span>
              </button>
            );
          })}
        </div>
        {persistError && <div className={styles.error}>{persistError}</div>}
      </div>
    );
  }

  if (isCompactVariant) {
    return (
      <div className={`${styles.container} ${styles.compactVariant}`}>
        <div className={styles.compactRow}>
          <div className={styles.compactLeft}>
            {!disabled && !isPersisting && eligibleDimensions.map((dimension) => {
              const isOpen = openDimension === dimension.dimensionFqn;
              return (
                <div key={dimension.dimensionFqn} className={styles.compactDim} ref={isOpen ? activeRowRef : undefined}>
                  <button
                    type="button"
                    className={styles.dimChip}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleDimension(dimension.dimensionFqn, e.currentTarget.parentElement as HTMLDivElement);
                    }}
                    title={`Add ${dimension.name}`}
                  >
                    <span className={styles.dimChipLabel}>{dimension.name}</span>
                    <span className={styles.dimPlus}>+</span>
                  </button>
                  {renderDropdown(dimension, draftPattern.find(e => e.dimension_fqn === dimension.dimensionFqn))}
                </div>
              );
            })}
          </div>
          <div className={styles.compactRight}>
            {selectedChips.map(({ dimension, elementFqn, elementName }) => (
              <span key={`${dimension.dimensionFqn}:${elementFqn}`} className={styles.tagChip}>
                <span className={styles.tagChipLabel}>{elementName}</span>
                {!disabled && !disableRemove && (
                  <button
                    type="button"
                    className={styles.tagChipRemove}
                    onClick={() => handleRemoveChip(dimension, elementFqn)}
                    aria-label={`Remove ${elementName}`}
                    disabled={isPersisting}
                    style={isPersisting ? { opacity: 0, pointerEvents: 'none' } : undefined}
                  >
                    <CloseIcon fontSize="inherit" />
                  </button>
                )}
              </span>
            ))}
          </div>
        </div>
        {persistError && <div className={styles.error}>{persistError}</div>}
      </div>
    );
  }

  // Default variant: same chip UI as compact but in two rows
  return (
    <div className={styles.container}>
      <div className={styles.defaultRow}>
        <div className={styles.compactLeft}>
          {!disabled && !isPersisting && eligibleDimensions.map((dimension) => {
            const isOpen = openDimension === dimension.dimensionFqn;
            return (
              <div key={dimension.dimensionFqn} className={styles.compactDim} ref={isOpen ? activeRowRef : undefined}>
                <button
                  type="button"
                  className={styles.dimChip}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleDimension(dimension.dimensionFqn, e.currentTarget.parentElement as HTMLDivElement);
                  }}
                  title={`Add ${dimension.name}`}
                >
                  <span className={styles.dimChipLabel}>{dimension.name}</span>
                  <span className={styles.dimPlus}>+</span>
                </button>
                {renderDropdown(dimension, draftPattern.find(e => e.dimension_fqn === dimension.dimensionFqn))}
              </div>
            );
          })}
        </div>
        <div className={styles.compactRight}>
          {selectedChips.map(({ dimension, elementFqn, elementName }) => (
            <span key={`${dimension.dimensionFqn}:${elementFqn}`} className={styles.tagChip}>
              <span className={styles.tagChipLabel}>{elementName}</span>
              {!disabled && !disableRemove && (
                <button
                  type="button"
                  className={styles.tagChipRemove}
                  onClick={() => handleRemoveChip(dimension, elementFqn)}
                  aria-label={`Remove ${elementName}`}
                  disabled={isPersisting}
                  style={isPersisting ? { opacity: 0, pointerEvents: 'none' } : undefined}
                >
                  <CloseIcon fontSize="inherit" />
                </button>
              )}
            </span>
          ))}
        </div>
      </div>
      {persistError && <div className={styles.error}>{persistError}</div>}
    </div>
  );
};

export default TagContextSelector;
